import { useTenantedClient } from '../stores/org';
import { useTenantedQueryKey } from './use-tenanted-query-key';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { Assert } from '@cotera/utilities';
import { queryKeys } from './query-cache-keys';

export type Tool = {
  id: string;
  name: string;
  displayName: string;
  toolConnectionId: string;
  enabled: boolean;
  createdAt: Date | string;
};

export type ExistingConnection = {
  id: string;
  provider: string;
  displayName: string;
  tools: Tool[];
};

export const useExistingConnections = (): { data: ExistingConnection[] } => {
  const client = useTenantedClient()['tools'];
  const toolsQueryKey = useTenantedQueryKey((orgId) =>
    queryKeys.tools.all({ orgId })
  );
  const connectionsQueryKey = useTenantedQueryKey((orgId) =>
    queryKeys.connections.all({ orgId })
  );

  const { data: tools } = useSuspenseQuery({
    queryKey: toolsQueryKey,
    queryFn: async () => {
      return Assert.assertOk(await client.list({}));
    },
  });

  const { data: connections } = useSuspenseQuery({
    queryKey: connectionsQueryKey,
    queryFn: async () => {
      return Assert.assertOk(await client.listConnections({}));
    },
  });

  const existingConnections = useMemo(() => {
    return connections.map((connection) => {
      const providerTools = tools
        .filter((tool) => tool.toolConnectionId === connection.id)
        .map((tool) => ({
          ...tool,
          enabled: connection.enabled,
          createdAt: connection.createdAt,
        }));

      return {
        ...connection,
        tools: providerTools,
      };
    });
  }, [tools, connections]);

  return { data: existingConnections };
};
